import { NOTARY_KEYWORDS_TOP, NOTARY_KEYWORDS_TOP_TOTAL } from "@/content/notary-corpus/top.gen";
import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildServiceListSchema,
  buildSpeakableSchema,
} from "@/lib/schema";

import { NOTARY_FAQ_CATEGORIES } from "@/content/notary-corpus/faq-defs";
import { NOTARY_PROVINCE_LANDINGS } from "@/content/notary-corpus/provinces";
import { NOTARY_COUNTRY_LANDINGS } from "@/content/notary-corpus/countries";
import { NOTARY_DOC_LANDINGS } from "@/content/notary-corpus/documents";
import { FaqSourceNote } from "@/components/FaqSourceNote";

export const Route = createFileRoute("/en/notary/")({
  loader: async (): Promise<{ NOTARY_KEYWORDS: string[]; NOTARY_FAQ_EN: { q: string; a: string }[] }> => {
    const [m0, m1] = await Promise.all([
      import("@/content/notary-corpus/en"),
      import("@/content/notary-corpus/keywords"),
    ]);
    return { NOTARY_KEYWORDS: m1.NOTARY_KEYWORDS, NOTARY_FAQ_EN: m0.NOTARY_FAQ_EN };
  },
  component: EnNotaryHub,
  head: ({ loaderData }) => {
    const { NOTARY_KEYWORDS = [], NOTARY_FAQ_EN = [] } = loaderData ?? {};

    const site = getActiveSiteUrl();
    const title = `Notary Public Thailand 2026 — Complete Hub for Expats`;
    const description = `Full hub for Notarial Services in Thailand — Apostille (Hague 2026), MFA legalization, embassy authentication, Power of Attorney, affidavits, certified translation. ${NOTARY_FAQ_EN.length}+ FAQs answered by licensed Notarial Services Attorneys.`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/en/notary",
        type: "website",
        locale: "en_US",
        alternates: [{ lang: "th", path: "/notary" }],
        keywords: NOTARY_KEYWORDS_TOP.slice(0, 80),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: site },
              { name: "Notary Public", url: `${site}/en/notary` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildServiceListSchema([
              { name: "Notary Public / Notarial Services Attorney", description: "Signature witness, certified true copy, affidavit, POA by licensed Thai Notarial Services Attorneys", url: "/en/services/notary-public" },
              { name: "Apostille & Hague Legalization", description: "Hague Apostille service for Thai documents (Thailand joined the Hague Convention 2026)", url: "/en/services/hague-apostille-2026" },
              { name: "MFA Legalization", description: "Thai Ministry of Foreign Affairs document legalization", url: "/en/services/mfa-legalization" },
              { name: "Certified Translation", description: "Certified & sworn translation for MFA and embassy submission", url: "/en/services/certified-translation" },
              { name: "Cross-Border Power of Attorney", description: "POA drafting for US, EU, Australia, Singapore recognition", url: "/en/services/notary-poa-cross-border" },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/notary")) },
      ],
    };
  },
});

const RELATED_INSIGHTS_EN = [
  { slug: "notary-public-thailand-expat-guide", title: "Notary Public Thailand — The Expat Guide 2026" },
  { slug: "notarial-services-attorney-thailand", title: "Notarial Services Attorney — What They Can & Cannot Do" },
  { slug: "apostille-thailand-2026", title: "Apostille Thailand 2026 — Country List & Steps" },
  { slug: "mfa-legalization-step-by-step", title: "MFA Legalization Step-by-Step" },
  { slug: "notarized-poa-abroad-jurisdiction-map", title: "Notarized POA Abroad — Jurisdiction Map" },
  { slug: "certified-translation-thailand-guide", title: "Certified Translation Thailand — Complete Guide" },
];

function EnNotaryHub() {
  const { NOTARY_KEYWORDS, NOTARY_FAQ_EN } = Route.useLoaderData() as { NOTARY_KEYWORDS: string[]; NOTARY_FAQ_EN: { q: string; a: string }[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <span>Notary Public</span>
        </nav>

        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            Notary Public Thailand 2026 — Complete Hub for Expats
          </h1>
          <p className="text-muted-foreground text-lg">
            End-to-end Notarial Services in Thailand — signature witness, certified true copy, affidavit,
            Power of Attorney, Apostille, MFA legalization, and embassy authentication. Licensed Notarial
            Services Attorneys registered with the Lawyers Council of Thailand. Nationwide (77 provinces).
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            <a href="/en/services/notary-public" className="rounded-md bg-primary px-4 py-2 text-primary-foreground font-medium">View core service →</a>
            <a href="/en/contact" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">Free consultation</a>
            <a href="/notary" className="rounded-md border px-4 py-2 text-sm text-muted-foreground hover:bg-muted">ภาษาไทย →</a>
          </div>
        </header>

        <section aria-label="Notary FAQ hub" className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="text-xl font-bold mb-2">{NOTARY_FAQ_EN.length}+ FAQs answered</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Browse by category — answered by licensed Notarial Services Attorneys, updated for 2026.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {NOTARY_FAQ_CATEGORIES.map((c) => (
              <a key={c.slug} href={`/en/notary/faq/${c.slug}`} className="rounded-lg border bg-background p-3 text-sm hover:border-primary hover:shadow-sm transition">
                <div className="font-semibold mb-1">{c.titleEn}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descEn}</div>
              </a>
            ))}
          </div>
          <div className="mt-4">
            <a href="/en/notary/faq" className="text-sm text-primary underline">View all FAQs →</a>
          </div>
        </section>

        <section aria-label="Notary by document type" className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="text-xl font-bold mb-2">Notarisation by document type</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Each page sets out exactly what the attorney certifies, what to bring, the mistakes that cause rejections, and the legalisation steps that follow.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {NOTARY_DOC_LANDINGS.map((d) => (
              <a key={d.slug} href={`/en/notary/document/${d.slug}`} className="rounded-lg border bg-background p-3 text-sm hover:border-primary hover:shadow-sm transition">
                <div className="font-semibold mb-1">{d.titleEn}</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{d.officialEn}</div>
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Notary by province" className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="text-xl font-bold mb-2">Service areas — notary public by province</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Each page covers the documents most often notarised locally, how appointments and document handling work, and the full turnaround including legalisation.
          </p>
          <div className="flex flex-wrap gap-2 text-sm">
            {NOTARY_PROVINCE_LANDINGS.map((p) => (
              <a key={p.slug} href={`/en/notary/province/${p.slug}`} className="rounded-full border bg-background px-3 py-1 text-muted-foreground hover:bg-muted">
                Notary Public {p.provinceEn}
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Notary by destination country" className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="text-xl font-bold mb-2">By destination country — where the document will be used</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Each page sets out the legalisation chain, language requirements, common rejection causes, and what changes when
            the Apostille Convention enters into force for Thailand on 28 February 2027.
          </p>
          <div className="flex flex-wrap gap-2 text-sm">
            {NOTARY_COUNTRY_LANDINGS.map((c) => (
              <a key={c.slug} href={`/en/notary/country/${c.slug}`} className="rounded-full border bg-background px-3 py-1 text-muted-foreground hover:bg-muted">
                Documents for {c.nameEn}
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Related services" className="mb-10">
          <h2 className="text-xl font-bold mb-3">Related Services</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { slug: "notary-public", title: "Notary Public / Notarial Services Attorney", desc: "Signature witness, certified true copy, affidavit, POA" },
              { slug: "hague-apostille-2026", title: "Apostille & Hague Legalization 2026", desc: "Thailand acceded on 30 June 2026 — in force from 28 February 2027" },
              { slug: "mfa-legalization", title: "MFA / Consular Legalization", desc: "Thai Ministry of Foreign Affairs authentication" },
              { slug: "notary-poa-cross-border", title: "Cross-Border Power of Attorney", desc: "POA for US, EU, Australia, Singapore recognition" },
              { slug: "certified-translation", title: "Certified Translation", desc: "Attorney / MOJ / NAATI-grade certified translation" },
              { slug: "notary-e-signature", title: "E-Signature & Remote Notary", desc: "ETDA-compliant electronic signature notarization" },
            ].map((s) => (
              <a key={s.slug} href={`/en/services/${s.slug}`} className="rounded-lg border bg-card p-4 hover:border-primary transition">
                <div className="font-semibold mb-1">{s.title}</div>
                <div className="text-sm text-muted-foreground">{s.desc}</div>
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Deep-dive insights" className="mb-10">
          <h2 className="text-xl font-bold mb-3">Deep-Dive Guides</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {RELATED_INSIGHTS_EN.map((it) => (
              <a key={it.slug} href={`/en/insights/${it.slug}`} className="rounded-lg border bg-card p-4 hover:border-primary transition">
                <div className="font-semibold text-sm">{it.title}</div>
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Featured questions" className="mb-10">
          <h2 className="text-xl font-bold mb-3">Top 10 Questions</h2>
          <div className="space-y-3">
            {NOTARY_FAQ_EN.slice(0, 10).map((item, i) => (
              <details key={i} className="rounded-lg border bg-card p-4">
                <summary className="cursor-pointer font-semibold text-sm">
                  <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
                </summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
              </details>
            ))}
          </div>
          <div className="mt-4">
            <a href="/en/notary/faq" className="text-sm text-primary underline">View all {NOTARY_FAQ_EN.length}+ FAQs →</a>
          </div>
        </section>

        <section aria-label="Keyword universe" className="mb-10">
          <h2 className="text-xl font-bold mb-3">Keyword universe ({NOTARY_KEYWORDS_TOP_TOTAL} terms)</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NOTARY_KEYWORDS_TOP.slice(0, 120).map((kw, i) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">{kw}</span>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">Fast response during office hours</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Free consultation with a licensed Notarial Services Attorney — Mon-Sat 9:00-18:00 ICT — English, Thai, Chinese, Japanese.
          </p>
          <a href="/en/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">Contact us →</a>
        </section>
        <div className="mx-auto max-w-4xl px-4">
          <FaqSourceNote topic="notary" locale="en" />
        </div>
      </main>
      <SiteFooter />
    </>
  );
}